import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Table, Alert } from 'react-bootstrap';
import { ASSET_DISPOSAL_API } from '@app/utils/apiUrl';
import { Get } from '@app/utils/httpProvider';
import { fetchWithAuth } from '@app/utils/httpClient';
import { toast } from 'react-toastify';
import { URI_INTERNAL } from '../../utils/pathLocations';
import { formatDateTimeMoment } from '../../utils/helper';
import Pagination from 'react-js-pagination';

const AssetDisposalDetail = () => {
    const { id } = useParams();
    const history = useHistory();

    const [page, setPage] = useState(1);
    const pageSize = 10;
    const [isExporting, setIsExporting] = useState(false);

    const handleExportExcel = async (disposalId, code) => {
        setIsExporting(true);
        try {
            const response = await fetchWithAuth(ASSET_DISPOSAL_API.EXPORT_EXCEL(disposalId), {
                method: 'GET'
            });

            if (response.ok) {
                const blob = await response.blob();
                const fileName = `PhieuDaoThai_${code || disposalId}.xlsx`;
                
                const url = window.URL.createObjectURL(new Blob([blob]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                toast.success('Tải phiếu đào thải thành công!');
            } else {
                toast.error('Có lỗi xảy ra khi tải file.');
            }
        } catch (error) {
            console.error('Lỗi export excel:', error);
            toast.error('Lỗi kết nối máy chủ.');
        } finally {
            setIsExporting(false);
        }
    };

    // 1. Lấy thông tin phiếu đào thải
    const { isLoading: isLoadingDisposal, data: disposalData } = useQuery(
        ['assetDisposalDetail', id],
        () => Get(`${ASSET_DISPOSAL_API.GET_ITEM}/${id}`)
    );

    // 2. Lấy danh sách tài sản trong phiếu
    const { isLoading: isLoadingAssets, data: assetsData } = useQuery(
        ['assetDisposalAssets', id],
        () => Get(`${ASSET_DISPOSAL_API.GET_ASSETS}/${id}`)
    );

    const allAssets = assetsData || [];
    const currentAssets = allAssets.slice((page - 1) * pageSize, page * pageSize);

    const goBack = () => {
        history.push(URI_INTERNAL.INVENTORY_SESSION_PATH);
    };

    if (isLoadingDisposal) return <div className="p-4 text-center">Đang tải thông tin...</div>;
    if (!disposalData) return <div className="p-4 text-center text-danger">Không tìm thấy thông tin phiếu đào thải.</div>;

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-lg-12">
                    {disposalData.status === 2 && (
                        <Alert variant="danger" className="border-danger shadow-sm mb-3">
                            <i className="fas fa-exclamation-triangle mr-2"></i>
                            Phiếu đào thải này đã bị hủy.
                            <br />
                            <small className="text-muted">Các tài sản đã được khôi phục trạng thái.</small>
                        </Alert>
                    )}

                    <div className="card border-0 shadow-sm mb-4">
                        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                            <h5 className="card-title fw-bold text-primary mb-0">
                                <i className="fas fa-info-circle mr-2"></i> 
                                Chi tiết Phiếu đào thải: {disposalData.disposalCode}
                            </h5>
                            <div className="ml-auto d-flex">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-success mr-2"
                                    onClick={() => handleExportExcel(disposalData.id, disposalData.disposalCode)}
                                    disabled={isExporting}
                                >
                                    {isExporting ? <i className="fas fa-spinner fa-spin mr-1"></i> : <i className="fas fa-file-excel mr-1"></i>} 
                                    Xuất Excel
                                </button>
                                <button type="button" className="btn btn-sm btn-secondary" onClick={goBack}>
                                    <i className="fas fa-arrow-left mr-1"></i> Quay lại
                                </button>
                            </div>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-6">
                                    <p className="mb-2"><strong>Tiêu đề:</strong> {disposalData.title}</p>
                                    <p className="mb-2"><strong>Người tạo:</strong> {disposalData.createdBy}</p>
                                    <p className="mb-2"><strong>Tổng số tài sản đào thải:</strong> <span className="font-weight-bold text-danger">{disposalData.totalAssets}</span></p>
                                </div>
                                <div className="col-md-6">
                                    <p className="mb-2">
                                        <strong>Trạng thái:</strong>{' '}
                                        {disposalData.status === 1 ? (
                                            <span className="badge badge-success px-2 py-1">Đã chốt</span>
                                        ) : disposalData.status === 2 ? (
                                            <span className="badge badge-danger px-2 py-1">Đã hủy</span>
                                        ) : (
                                            <span className="badge badge-secondary px-2 py-1">Nháp</span>
                                        )}
                                    </p>
                                    <p className="mb-2"><strong>Ngày đào thải:</strong> {formatDateTimeMoment(disposalData.disposalDate)}</p>
                                    <p className="mb-2"><strong>Lý do đào thải:</strong> {disposalData.reason || '-'}</p>
                                </div>
                                <div className="col-md-12 mt-2">
                                    <p className="mb-1"><strong>Ghi chú:</strong></p>
                                    <div className="p-2 bg-light border rounded" style={{ minHeight: '40px' }}>
                                        {disposalData.notes ? (
                                            <span dangerouslySetInnerHTML={{ __html: disposalData.notes }}></span>
                                        ) : (
                                            <em className="text-muted">Không có ghi chú</em>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-0">
                            {isLoadingAssets ? (
                                <div className="p-4 text-center">Đang tải danh sách tài sản...</div>
                            ) : (
                                <div className="table-responsive">
                                    <Table bordered hover size="sm" className="mb-0">
                                        <thead className="thead-light">
                                            <tr>
                                                <th className="text-center" style={{ width: '60px' }}>STT</th>
                                                <th>Mã TS</th>
                                                <th>Tên TS</th>
                                                <th>Khoa phòng</th>
                                                <th>Cấp chất lượng</th>
                                                <th className="text-right">Nguyên giá</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentAssets.length > 0 ? (
                                                currentAssets.map((item, index) => (
                                                    <tr key={item.id} className="bg-white">
                                                        <td className="text-center align-middle">{(page - 1) * pageSize + index + 1}</td>
                                                        <td className="align-middle font-weight-bold text-primary">{item.assetCode}</td>
                                                        <td className="align-middle">{item.assetName}</td>
                                                        <td className="align-middle">{item.departmentName || '-'}</td>
                                                        <td className="align-middle text-danger font-weight-bold">{item.qualityLevelName || '-'}</td>
                                                        <td className="align-middle text-right">{item.purchaseValue ? item.purchaseValue.toLocaleString('vi-VN') : '-'} đ</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="6" className="text-center py-4 text-muted">
                                                        Không có tài sản nào trong phiếu này.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            )}

                            {allAssets.length > 0 && (
                                <div className="d-flex justify-content-center p-3 border-top bg-light">
                                    <Pagination
                                        itemClass="page-item"
                                        linkClass="page-link"
                                        innerClass="pagination pagination-sm mb-0"
                                        activePage={page}
                                        itemsCountPerPage={pageSize}
                                        totalItemsCount={allAssets.length}
                                        pageRangeDisplayed={5}
                                        onChange={(num) => setPage(num)}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AssetDisposalDetail;
